import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { QuotaInfo } from "../../shared/models/quota-info.model";
import { MealSuggestionsResponse } from "../../shared/models/meal-planner.model";
import { PlannedMeal, PlannedMealWithRecipe, PlannedMealSlotType } from "../../shared/models/planned-meal.model";

@Injectable({ providedIn: 'root' })
export class MealPlannerService {
  private static readonly ONGOING_MEAL_URL = `${environment.apiUrl}/ongoing-meals`;
  private static readonly MEAL_PLANNER_URL = `${environment.apiUrl}/meal-planner`;

  private normalizePlanDate(planDate: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(planDate)) {
      return planDate;
    }

    const parsed = new Date(planDate);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    return planDate;
  }

  constructor(private http: HttpClient) {}

  startMeal(data: {
    recipeId: number;
    servings: number;
  }): Observable<any> {
    return this.http.post(MealPlannerService.ONGOING_MEAL_URL, data);
  }

  // For later use:
  getUserOngoingMeals(): Observable<any> {
    return this.http.get(MealPlannerService.ONGOING_MEAL_URL);
  }

  completeMeal(id: number): Observable<any> {
    return this.http.patch(`${MealPlannerService.ONGOING_MEAL_URL}/${id}/complete`, {});
  }

  getMealQuota(): Observable<QuotaInfo> {
    return this.http.get<QuotaInfo>(`${MealPlannerService.ONGOING_MEAL_URL}/quota`);
  }

  getPlannedMealsQuota(householdId: number, fromDate?: string, toDate?: string): Observable<QuotaInfo> {
    let params = new HttpParams();
    if (fromDate) {
      params = params.set('from', fromDate);
    }
    if (toDate) {
      params = params.set('to', toDate);
    }
    return this.http.get<QuotaInfo>(
      `${environment.apiUrl}/households/${householdId}/meal-plans/quota`,
      { params }
    );
  }

  getMealSuggestions(options: {
    breakfast?: boolean;
    lunch?: boolean;
    dinner?: boolean;
    snack?: boolean;
  }): Observable<MealSuggestionsResponse> {
    const params = new HttpParams()
      .set('breakfast', String(!!options.breakfast))
      .set('lunch', String(!!options.lunch))
      .set('dinner', String(!!options.dinner))
      .set('snack', String(!!options.snack))

    return this.http.get<MealSuggestionsResponse>(
      `${MealPlannerService.MEAL_PLANNER_URL}/suggestions`,
      { params }
    );
  }

  // Planned Meals
  getPlannedMeals(
    householdId: number,
    fromDate: string,
    toDate: string
  ): Observable<PlannedMealWithRecipe[]> {
    const params = new HttpParams()
      .set('from', fromDate)
      .set('to', toDate);

    return this.http.get<PlannedMealWithRecipe[]>(
      `${environment.apiUrl}/households/${householdId}/meal-plans`,
      { params }
    );
  }

  deletePlannedMeal(
    householdId: number,
    plan_date: string,
    slot_type: PlannedMealSlotType,
    planned_meal_id?: number
  ): Observable<any> {
    const normalizedPlanDate = this.normalizePlanDate(plan_date);
    let params = new HttpParams()
      .set('plan_date', normalizedPlanDate)
      .set('slot_type', slot_type);

    if (planned_meal_id != null) {
      params = params.set('planned_meal_id', String(planned_meal_id));
    }

    return this.http.delete(
      `${environment.apiUrl}/households/${householdId}/meal-plans`,
      { params }
    );
  }

  upsertPlannedMeal(
    householdId: number,
    plan_date: string,
    slot_type: PlannedMealSlotType,
    recipe_id: number,
    note?: string
  ): Observable<PlannedMealWithRecipe> {
    const normalizedPlanDate = this.normalizePlanDate(plan_date);
    const body = {
      plan_date: normalizedPlanDate,
      slot_type,
      recipe_id,
      ...(note && { note })
    };

    return this.http.put<PlannedMealWithRecipe>(
      `${environment.apiUrl}/households/${householdId}/meal-plans`,
      body
    );
  }

  updatePlannedMealStatus(
    householdId: number,
    plannedMealId: number,
    status: 'STARTED' | 'DONE'
  ): Observable<PlannedMeal> {
    return this.http.patch<PlannedMeal>(
      `${environment.apiUrl}/households/${householdId}/meal-plans/${plannedMealId}/status`,
      { status }
    );
  }
}